/**
 * Coop Green Homes — Housing Project Workspace
 * Route: /housing-project
 *
 * Visual standard: matches SME (Gasavant) exactly.
 * Same hierarchy, same spacing, same card discipline.
 * Different use case, identical platform feel.
 */
import React, { useState, useEffect } from "react";
import { useHousingStatus } from "@/hooks/useHousingStatus";
import { useHousingJourney, invalidateHousingCache } from "@/hooks/useHousingJourney";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  CheckCircle2, TrendingDown, Building2, FileText, Download,
  RefreshCw, ArrowRight, GitBranch, Shield, Layers, Zap,
  ChevronRight, Target, DollarSign, Info
} from "lucide-react";

// ── KPI Card — exact match to SME KPICard ────────────────────────────────
function KPICard({ icon: Icon, iconBg, iconColor, value, unit, label, sub }) {
  return (
    <MetricCard label={label} value={value ?? "—"} unit={unit} sub={sub} icon={Icon} iconBg={iconBg} />
  );
}

// ── Climate Snapshot — clean, SME-grade ────────────────────────────────────
function ClimateSnapshot({ journey, navigate }) {
  const demo = journey?.demo_unit || {};
  const mats = journey?.material_breakdown || [];
  const wtm  = journey?.what_this_means || {};
  const bm   = journey?.industry_benchmark || {};

  const MAT_COLOR = { cement: "bg-orange-500", masonry: "bg-amber-400", steel: "bg-blue-500" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="climate-snapshot">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
            Embodied Carbon — Per Unit
          </p>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-slate-900 tabular-nums leading-none">
              {demo.embodied_reduction_tco2e}
            </span>
            <span className="text-base text-slate-400 mb-1">tCO₂e saved</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {demo.baseline_embodied_tco2e}t baseline → {demo.project_embodied_tco2e}t project
          </p>
        </div>
        <button onClick={() => navigate && navigate("/housing-project")}
          className="flex items-center gap-1 text-[11px] text-teal-600 font-semibold hover:underline">
          Details <ChevronRight size={12} />
        </button>
      </div>

      {/* Material bars — exactly like SME source bars */}
      <div className="space-y-2.5 mb-5">
        {mats.map(m => (
          <div key={m.category}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600">{m.material}</span>
              <span className="font-bold text-slate-900">{m.baseline_tco2e}t · {m.baseline_pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${MAT_COLOR[m.category] || "bg-teal-500"} transition-all duration-700`}
                style={{ width: `${m.baseline_pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Key insight — dark card, same as SME */}
      {wtm.biggest_source && (
        <div className="bg-[#0F172A] rounded-xl px-4 py-3 mb-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-0.5">Key Insight</p>
          <p className="text-xs text-white">{wtm.biggest_source}</p>
          {wtm.so_what && <p className="text-xs text-teal-400 mt-1">{wtm.so_what}</p>}
        </div>
      )}

      {/* Operational — ONE clean row, not competing section */}
      <div className="flex items-center justify-between py-2.5 border-t border-slate-100">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Operational reduction</p>
          <p className="text-xs text-slate-500 mt-0.5">Annual recurring · solar + insulation</p>
        </div>
        <span className="text-sm font-bold text-blue-600">{demo.operational_reduction_tco2e_yr} tCO₂e/yr</span>
      </div>

      {/* Benchmark — same dark card as SME */}
      {bm.label && (
        <div className="bg-slate-900 rounded-xl px-4 py-3 mt-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">
              vs Industry
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-white">{demo.project_embodied_tco2e}</span>
              <span className="text-xs text-slate-400">tCO₂e/unit</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Conventional: <span className="text-slate-300 font-semibold">{bm.avg_range}</span>
            </p>
          </div>
          <span className="text-xs font-bold px-2.5 py-1.5 rounded-lg flex-shrink-0 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            {bm.label}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Right sidebar: Key Numbers (simple, no duplication) ────────────────────
function KeyNumbers({ journey }) {
  const demo = journey?.demo_unit || {};
  const prog = journey?.programme || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="key-numbers">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
        Programme Summary
      </p>
      <div className="space-y-3">
        {[
          ["Embodied reduction", `${prog.total_embodied_tco2e?.toLocaleString()} tCO₂e`, "Construction phase, one-time", "text-orange-600"],
          ["Annual operational", `${prog.annual_operational_tco2e?.toLocaleString()} tCO₂e/yr`, "Recurring annual benefit", "text-blue-600"],
          ["Programme capex", `$${((prog.capex_total_usd||0)/1000000).toFixed(1)}M`, "Incremental over conventional", "text-slate-900"],
          ["Energy savings", `$${prog.energy_savings_total_usd_yr?.toLocaleString()}/yr`, "Annual cost reduction", "text-emerald-600"],
        ].map(([l, v, sub, c]) => (
          <div key={l} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-slate-500">{l}</p>
              <p className="text-xs text-slate-400 italic">{sub}</p>
            </div>
            <span className={`text-sm font-bold flex-shrink-0 ${c}`}>{v}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 italic mt-3 pt-2 border-t border-slate-100">
        {prog.units_count?.toLocaleString()} units · Indicative estimate
      </p>
    </div>
  );
}

// ── Financing Signal ────────────────────────────────────────────────────────
function FinancingSignal({ journey }) {
  const fr = journey?.financing_readiness || {};
  const demo = journey?.demo_unit || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="financing-signal">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Financing Signal</p>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <p className="text-sm font-bold text-slate-900">{fr.signal || "MODERATE"}</p>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{fr.notes}</p>
      <div className="space-y-1.5 border-t border-slate-100 pt-2.5">
        {[
          ["Cost/tCO₂e", `$${demo.cost_per_tco2e} (IFC EDGE range)`, "text-teal-700"],
          ["Energy savings", `$${demo.energy_savings_usd_yr}/unit/yr`, "text-emerald-700"],
        ].map(([l, v, c]) => (
          <div key={l} className="flex justify-between text-xs">
            <span className="text-slate-400">{l}</span>
            <span className={`font-semibold ${c}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Actions — clean separation, no visual noise ────────────────────────────
function ActionsSection({ journey }) {
  const [expanded, setExpanded] = useState(null);
  const actions = journey?.actions || [];
  const embodied    = actions.filter(a => a.carbon_type === "embodied");
  const operational = actions.filter(a => a.carbon_type === "operational");

  const renderCard = (a, idx) => {
    const isOpen = expanded === idx;
    const CatIcon = a.category === "energy" ? Zap : Layers;
    return (
      <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`action-card-${idx}`}>
        <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
          onClick={() => setExpanded(isOpen ? null : idx)}>
          <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <CatIcon size={13} className="text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-900 truncate">{a.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {a.impact_tco2e} {a.carbon_type === "operational" ? "tCO₂e/yr" : "tCO₂e"} saved
            </p>
          </div>
        </button>
        {isOpen && (
          <div className="px-4 pb-3 pt-1 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-600 mb-2">{a.description}</p>
            {a.operational_note && (
              <p className="text-xs text-slate-500 italic">{a.operational_note}</p>
            )}
            {a.sdg_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {a.sdg_tags.map(s => (
                  <span key={s.id} className="text-xs font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{s.name}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="actions-section">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">Actions</p>

      {/* Embodied — primary */}
      <div className="mb-4">
        <p className="text-xs text-orange-600 font-bold uppercase tracking-wider mb-2">
          Embodied Carbon · Construction Phase · One-Time
        </p>
        <div className="space-y-2">
          {embodied.map((a, i) => renderCard(a, i))}
        </div>
      </div>

      {/* Operational — secondary, clearly separated */}
      {operational.length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2">
            Operational · Annual Recurring · Additional Benefit
          </p>
          <div className="space-y-2">
            {operational.map((a, i) => renderCard(a, embodied.length + i))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SDG Table — identical to SME ───────────────────────────────────────────
function SDGTable({ journey }) {
  const sdgs = journey?.sdg_tags || [];
  const REL_COLOR = { High: "text-emerald-700 bg-emerald-50", Medium: "text-amber-700 bg-amber-50" };
  if (!sdgs.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="sdg-section">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
        Sustainability Contribution (SDGs)
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left text-xs text-slate-400 uppercase pb-2 pr-3">SDG</th>
            <th className="text-left text-xs text-slate-400 uppercase pb-2 pr-3">Relevance</th>
            <th className="text-left text-xs text-slate-400 uppercase pb-2">Driver</th>
          </tr>
        </thead>
        <tbody>
          {sdgs.map(s => (
            <tr key={s.id} className="border-b border-slate-50">
              <td className="py-1.5 pr-3">
                <p className="font-bold text-slate-900 text-xs">{s.name}</p>
                <p className="text-xs text-slate-400 leading-tight">{s.title}</p>
              </td>
              <td className="py-1.5 pr-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${REL_COLOR[s.relevance] || REL_COLOR.Medium}`}>
                  {s.relevance}
                </span>
              </td>
              <td className="py-1.5 text-xs text-slate-500">{s.driver}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Carbon Pathways — same discipline as SME ──────────────────────────────
function CarbonPathways({ navigate }) {
  const PATHWAYS = [
    { id: "insetting", rank: 1, name: "Insetting (Supply Chain)",
      badge: "High", badgeColor: "bg-emerald-100 text-emerald-700 border-emerald-300",
      reason: "Direct embodied carbon reduction within the construction value chain. Buyers claim the reduction as supply-chain decarbonization — no credit sales required." },
    { id: "vcm", rank: 2, name: "Voluntary Carbon Market (VCM)",
      badge: "Medium", badgeColor: "bg-amber-100 text-amber-700 border-amber-300",
      reason: "Programme-scale avoided emissions may be suitable if structured, verified, and aggregated. Requires additionality assessment and methodology validation." },
    { id: "poa", rank: 3, name: "Programmatic / PoA",
      badge: "Medium", badgeColor: "bg-amber-100 text-amber-700 border-amber-300",
      reason: "More suitable at 1,000-unit programme level. A Programme of Activities bundles multiple construction projects under standardized design and MRV." },
    { id: "article6", rank: 4, name: "Article 6 (Compliance Market)",
      badge: "Not eligible", badgeColor: "bg-red-50 text-red-600 border-red-200",
      reason: "No sovereign authorization and insufficient programme maturity. Future pathway possible through national alignment and formal MRV." },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="carbon-pathways">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">
        Carbon Market Pathways
      </p>
      <div className="space-y-2.5">
        {PATHWAYS.map(p => (
          <div key={p.id} className="border border-slate-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3"
            data-testid={`pathway-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs text-slate-400">#{p.rank}</span>
                <p className="text-xs font-bold text-slate-900">{p.name}</p>
              </div>
              <p className="text-xs text-slate-500 leading-snug">{p.reason}</p>
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ml-2 ${p.badgeColor}`}>
              {p.badge}
            </span>
          </div>
        ))}
      </div>
      <button onClick={() => navigate("/aggregation-enrollment")}
        className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 border border-teal-200 text-teal-700 text-xs font-bold rounded-xl hover:bg-teal-50 transition-colors"
        data-testid="aggregation-cta">
        <GitBranch size={12} /> Explore Aggregated Carbon Programme Pathway
      </button>
      <p className="text-xs text-slate-400 text-center mt-2">
        Pathway routing is indicative. Not a carbon eligibility determination.
      </p>
    </div>
  );
}

// ── Reports — 2 cards, identical to SME Reports ───────────────────────────
function ReportsSection({ journey, navigate }) {
  const [dl, setDl] = useState(null);
  const demo = journey?.demo_unit || {};
  const org  = journey?.organization || {};
  const rid  = journey?.report_id;

  const download = async (type) => {
    setDl(type);
    try {
      const token = localStorage.getItem("decarboniq_token");
      let res;
      if (type === "ccr") {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/housing/credentials-report`,
          { headers: { Authorization: `Bearer ${token}` } });
      } else {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/housing/finance-brief`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            project_name: "Coop Green Homes - 3-Bedroom Low-Carbon Demo Unit",
            developer_name: org.name || "Coop Green Homes Ltd",
            country: org.country || "Nigeria",
            brief_title: "HOUSING FINANCE BRIEF",
            embodied_reduction_tco2e:    demo.embodied_reduction_tco2e    || 1.64,
            operational_reduction_tco2e_yr: demo.operational_reduction_tco2e_yr || 1.13,
            total_lifetime_reduction_tco2e: demo.lifetime_reduction_tco2e  || 35.49,
            project_life_years: 30, units_count: 1000,
            incremental_cost_usd: (demo.capex_usd || 4500) * 1000,
            cost_per_tco2e:      demo.cost_per_tco2e   || 126.8,
            energy_savings_yr:   demo.energy_savings_usd_yr || 182.7,
            benchmark_position:  "~20% lower embodied than conventional",
            mrv_readiness: "weak", operational_reduction_pct: 32, embodied_reduction_pct: 20,
          }),
        });
      }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = type === "ccr" ? "CoopGreenHomes-ClimateCredentials.pdf" : "CoopGreenHomes-FinanceBrief.pdf";
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(type === "ccr" ? "Climate Credentials downloaded." : "Finance Brief downloaded.");
    } catch { toast.error("Download failed — please retry."); }
    setDl(null);
  };

  const REPORTS = [
    { id: "ccr",   type: "ccr",  title: "Climate Credentials Report", subtitle: "Technical · Audit · Credibility",
      desc: "Embodied + operational breakdown, material methodology, benchmark, SDG alignment, audit trail, QR verification.", accent: true },
    { id: "brief", type: "brief", title: "Finance Brief", subtitle: "Bank-facing · Decision support",
      desc: "Per-unit metrics, programme scaling to 1,000 units, capex/savings summary, financing signal.", accent: false },
  ];

  return (
    <div className="space-y-3" data-testid="reports-section">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Reports</p>
      {REPORTS.map(r => (
        <div key={r.id} className={`bg-white border-2 rounded-xl p-5 ${r.accent ? "border-teal-200" : "border-slate-200"}`}
          data-testid={`report-card-${r.id}`}>
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${r.accent ? "bg-teal-50" : "bg-slate-50"}`}>
              <FileText size={18} className={r.accent ? "text-teal-600" : "text-slate-600"} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-sm font-bold text-slate-900">{r.title}</h3>
                <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{r.subtitle}</span>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                  <CheckCircle2 size={8} /> Ready
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{r.desc}</p>
            </div>
          </div>
          <button onClick={() => download(r.type)} disabled={dl === r.type}
            className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${
              r.accent ? "bg-[#0F172A] text-white hover:bg-slate-800" : "bg-slate-800 text-white hover:bg-slate-700"
            }`}
            data-testid={`download-${r.id}`}>
            {dl === r.type ? <><RefreshCw size={14} className="animate-spin" />Generating...</> : <><Download size={14} />{r.title}</>}
          </button>
        </div>
      ))}

      {rid && (
        <a href={`/verify/report/${rid}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 bg-white w-full justify-center"
          data-testid="verify-link">
          <Shield size={13} /> Verify on Public Registry
        </a>
      )}
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function CoopHousingWorkspace() {
  const navigate  = useNavigate();
  const [revisionDetails, setRevisionDetails] = useState(null);
  const { review_status: reviewStatus } = useHousingStatus();
  const { loading, journey, refresh } = useHousingJourney();

  // Load revision details when status requires it
  useEffect(() => {
    const wf = journey?.workflow_status || journey?.status || "";
    if (["requires_revision", "rejected"].includes(wf)) {
      client.get("/housing/revision-details").then(r => setRevisionDetails(r.data)).catch(() => {});
    }
  }, [journey]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const org  = journey?.organization || {};
  const proj = journey?.project      || {};
  const kpis = journey?.kpis         || {};
  const demo = journey?.demo_unit    || {};

  // Fix 1+7: Translate internal workflow states to user-friendly business language
  const wfState = journey?.workflow_status || journey?.status || "Assessed";
  const STATUS_DISPLAY = {
    draft:             { label: "Assessment Draft",           sub: "Complete inputs to proceed",                     color: "border-slate-200 bg-white",       dot: "bg-slate-400"  },
    calculated:        { label: "Project Profile Structured", sub: "Climate and value profile ready",               color: "border-slate-200 bg-white",       dot: "bg-teal-500"   },
    structured:        { label: "Ready to Submit",            sub: "Structured for verification review",            color: "border-teal-200 bg-teal-50",      dot: "bg-teal-500"   },
    submitted:         { label: "Under Review",               sub: "Awaiting verification — no changes during review", color: "border-blue-200 bg-blue-50",   dot: "bg-blue-500"   },
    requires_revision: { label: "Needs Revision",             sub: "Reviewer requested updates before verification", color: "border-amber-200 bg-amber-50",   dot: "bg-amber-500"  },
    rejected:          { label: "Needs Revision",             sub: "Reviewer requested updates before verification", color: "border-amber-200 bg-amber-50",   dot: "bg-amber-500"  },
    verified:          { label: "Verified",                   sub: "Platform workflow verified — internal only. Not third-party validated.", color: "border-emerald-300 bg-emerald-50", dot: "bg-emerald-500" },
    Assessed:          { label: "Project Profile Structured", sub: "Climate and value profile ready · Reports available", color: "border-slate-200 bg-white", dot: "bg-teal-500"   },
  };
  const statusDsp  = STATUS_DISPLAY[wfState] || STATUS_DISPLAY.Assessed;
  const isNegative = ["rejected", "requires_revision"].includes(wfState);

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="housing-workspace">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{org.name}</p>
          <PageHeader title={proj?.project_name || proj?.name || "Housing Project"} />
          <p className="text-xs text-slate-400 mt-0.5">{proj.methodology} · {org.country}</p>
        </div>
      </div>

      {/* ── Status Banner — Fix 1+7: user-friendly language, no contradictions ── */}
      <div className={`border rounded-xl px-6 py-4 mb-6 ${statusDsp.color}`}
        data-testid="readiness-banner">
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isNegative ? "bg-amber-500 border-amber-500" : "bg-teal-500 border-teal-500"} border-2`}>
            {isNegative
              ? <span className="text-white text-xs font-bold">!</span>
              : <CheckCircle2 size={14} className="text-white" />
            }
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-700">{statusDsp.label}</p>
            <p className={`text-xs mt-0.5 ${isNegative ? "text-amber-600" : "text-teal-600"}`}>
              {statusDsp.sub}
            </p>
          </div>
          <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${isNegative ? "bg-amber-100 text-amber-700" : "bg-teal-100 text-teal-700"}`}>
            {statusDsp.label}
          </span>
        </div>
      </div>

      {/* ── Actionable Revision Block — Fix 1 ── */}
      {isNegative && revisionDetails?.actionable && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 mb-6" data-testid="revision-action-block">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-bold text-amber-800">Action Required — Reviewer Feedback</p>
              <p className="text-xs text-amber-600 mt-0.5">
                {revisionDetails.reviewer} · {revisionDetails.reviewer_date}
              </p>
            </div>
            <span className="text-xs font-bold px-2 py-1 bg-amber-200 text-amber-800 rounded-full">
              {revisionDetails.missing_evidence_count > 0 ? `${revisionDetails.missing_evidence_count} items missing` : "Review required"}
            </span>
          </div>

          {/* Issues list */}
          {revisionDetails.issues?.length > 0 && (
            <div className="space-y-1.5 mb-4">
              {revisionDetails.issues.map((issue, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-800">
                  <span className="text-amber-500 font-bold flex-shrink-0">·</span>
                  {issue.text}
                </div>
              ))}
            </div>
          )}
          {!revisionDetails.issues?.length && (
            <p className="text-xs text-amber-700 mb-4">
              The reviewer has returned the submission for updates. Review your evidence and inputs, then resubmit.
            </p>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap gap-2">
            {revisionDetails.next_steps?.slice(0, 2).map((step, i) => (
              <button key={i} onClick={() => navigate(step.path)}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl transition-colors ${
                  i === 0
                    ? "bg-amber-600 text-white hover:bg-amber-700"
                    : "border border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                }`}
                data-testid={`revision-cta-${i}`}>
                {step.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── 4 KPI Cards — Fix 7: no raw rejected/requires_revision in KPI ── */}      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard icon={TrendingDown} iconBg="bg-teal-50" iconColor="text-teal-600"
          value={`~${(kpis.programme_impact_tco2e || 5000).toLocaleString()}`}
          unit="tCO₂e" label="Programme Impact (3yr)" sub="Indicative estimate" />
        <KPICard icon={Building2} iconBg="bg-emerald-50" iconColor="text-emerald-600"
          value={`~${kpis.per_unit_reduction_tco2e || 5}`}
          unit="tCO₂e/unit" label="Per Unit (3yr)" sub="Embodied + operational" />
        <KPICard icon={Layers} iconBg="bg-blue-50" iconColor="text-blue-600"
          value={kpis.project_type || "Low-Carbon Housing"}
          label="Project Type" sub={proj.methodology} />
        <KPICard icon={Shield} iconBg="bg-amber-50" iconColor="text-amber-600"
          value={reviewStatus}
          label="Status" sub={`$${demo.cost_per_tco2e}/tCO₂e cost efficiency`} />
      </div>

      {/* ── Main 2-col layout — same as SME ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Left: 2/3 */}
        <div className="lg:col-span-2 space-y-5">
          <div id="snapshot"><ClimateSnapshot journey={journey} navigate={navigate} /></div>
          <div id="actions"><ActionsSection journey={journey} /></div>
          <div id="sdg"><SDGTable journey={journey} /></div>
          <div id="pathways"><CarbonPathways navigate={navigate} /></div>
        </div>

        {/* Right: 1/3 */}
        <div className="space-y-4">
          <KeyNumbers journey={journey} />
          <FinancingSignal journey={journey} />
          <div id="reports"><ReportsSection journey={journey} navigate={navigate} /></div>
        </div>
      </div>

      {/* ── Footnote disclaimer — small, not competing ── */}
      <p className="text-xs text-slate-400 italic text-center pb-4">
        {journey?.disclaimer}
        {" "}Demo unit: {proj.demo_unit}
      </p>
    </div>
  );
}
